import React, { useRef, useEffect, useMemo, forwardRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Autoplay } from 'swiper/modules';
import themes from 'styles/themes';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './styles.scss';
import EngagingSliderPreview from './EngagingSliderPreview';
import { TRACK_EVENT, trackEvent } from 'utils/analytics';
import { useChatContext } from '../hooks/ChatContext';
import { ProductTypeEnum } from '__generated__/globalTypes';
import { STRINGS } from '../../../constants/strings';
import _isEmpty from 'lodash/isEmpty';

interface EngagingSliderProps {
    title?: string;
    subtitle?: string;
    products: any[];
    setEngageSliderState: (value: boolean) => void;
    isEngageSliderPreviewOpen: boolean;
    conversationId?: string | null;
}

const useStyles = makeStyles(() => ({
    container: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        margin: '16px 0',
        border: '0.3px solid #D1D1D1',
    },
    header: {
        textAlign: 'center',
        marginBottom: '32px',
    },
    subtitle: {
        fontFamily: 'Montserrat',
        fontSize: '16px',
        fontWeight: 400,
        color: '#666',
        lineHeight: 1.4,
        maxWidth: '400px',
        margin: '0 auto',
    },
    swiperContainer: {
        position: 'relative',
        '&::before': {
            width: '101%',
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
                'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.2) 10%, transparent 10%, transparent 90%, rgba(255,255,255,0.2) 90%, rgba(255,255,255,1) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
        },
        '& .swiper': {
            paddingBottom: '50px',
        },
        '& .swiper-pagination': {
            bottom: '20px',
            '& .swiper-pagination-bullet': {
                backgroundColor: '#ddd',
                opacity: 1,
                '&.swiper-pagination-bullet-active': {
                    backgroundColor: themes.palette.primary.main,
                },
            },
        },
        '& .swiper-button-next, & .swiper-button-prev': {
            color: themes.palette.primary.main,
            width: '32px',
            height: '32px',
            marginTop: '-16px',
            '&::after': {
                fontSize: '16px',
            },
        },
    },
    productItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
        },
    },
    productImageContainer: {
        backgroundColor: 'transparent',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '8px 0px',
        overflow: 'hidden',
    },
    productImage: {
        width: '100%',
        height: '100%',
        objectFit: 'scale-down',
        borderRadius: '8px',
    },
    productTitle: {
        fontFamily: 'PoppinsRegular',
        fontSize: '11px',
        fontWeight: 500,
        color: themes.palette.primary.main,
        textAlign: 'center',
        lineHeight: 1.2,
        marginTop: '4px',
    },
    swiperSlide: {
        width: '180px', // Fixed width for each slide to control visibility
    },
}));

const EngagingSlider = forwardRef<HTMLDivElement, EngagingSliderProps>(
    (
        {
            title = 'While I prepare your recommendations...',
            subtitle = 'Here are some styles I loved lately. Let me know if any catch your eye.',
            products,
            isEngageSliderPreviewOpen,
            setEngageSliderState,
            conversationId,
        },
        ref,
    ) => {
        const classes = useStyles();
        const swiperRef = useRef<any>(null);
        const selectedStyle = useRef({});
        const { user } = useChatContext();

        const handleAnalytics = useCallback(
            (eventName, style, customProps) => {
                const userInfo = {
                    userId: user?.id,
                    userType: user?.userType,
                    email: user?.email,
                    gender: user?.gender,
                    name: user?.firstName,
                };
                let analyticsParams: any = {
                    ...userInfo,
                    conversationId: conversationId,
                    recommendationSource: STRINGS.STATIC_AMIRA,
                    numberOfStyles: products?.length,
                };
                if (!_isEmpty(style)) {
                    analyticsParams = {
                        ...analyticsParams,
                        productId: style?.id || '',
                        productTaffiId: style?.taffiId || '',
                        productType: ProductTypeEnum.style,
                    };
                }
                if (!_isEmpty(customProps)) {
                    analyticsParams = {
                        ...analyticsParams,
                        ...customProps,
                    };
                }
                trackEvent(eventName, analyticsParams);
            },
            [conversationId, user, products?.length],
        );

        const handleOpenStylePreview = (style) => {
            handleAnalytics(TRACK_EVENT.BUSY_INDICATOR_WITH_STYLES, style, {
                state: STRINGS.STYLE_CLICKED,
            });
            selectedStyle.current = style;
            setEngageSliderState(true);
        };

        const handleCloseStylePreview = (item) => {
            setEngageSliderState(false);
        };

        const autoplayEnabled = useMemo(() => {
            return !isEngageSliderPreviewOpen;
        }, [isEngageSliderPreviewOpen]);

        useEffect(() => {
            if (swiperRef?.current?.autoplay && products?.length) {
                if (autoplayEnabled) {
                    if (swiperRef.current.autoplay.paused || !swiperRef.current.autoplay.running) {
                        swiperRef.current.autoplay.start();
                    }
                } else {
                    if (swiperRef.current.autoplay.running) {
                        swiperRef.current.autoplay.stop();
                    }
                }
            }
        }, [products, autoplayEnabled]);

        useEffect(() => {
            handleAnalytics(
                TRACK_EVENT.BUSY_INDICATOR_WITH_STYLES,
                {},
                {
                    state: STRINGS.STYLES_DISPLAYED,
                },
            );
        }, [handleAnalytics]);

        return (
            <div className={`${classes.container} engaging-slider-container`} ref={ref} id="engaging-slider-container">
                <div className={classes.header}>
                    <h2 className="title">{title}</h2>
                    <p className="title title-caption">{subtitle}</p>
                </div>

                <div className={classes.swiperContainer}>
                    <Swiper
                        modules={[EffectCoverflow, Autoplay]}
                        slidesPerView={'auto'}
                        centeredSlides={true}
                        loop={products.length > 1}
                        effect={'coverflow'}
                        coverflowEffect={{
                            rotate: 0,
                            stretch: 0,
                            depth: 100,
                            modifier: 1,
                            slideShadows: false,
                        }}
                        watchSlidesProgress={true}
                        autoplay={
                            autoplayEnabled
                                ? {
                                    delay: 2000, // 2s between slides
                                    disableOnInteraction: false, // keep autoplay alive even when user interacts
                                    pauseOnMouseEnter: true, // pauses when mouse is over
                                }
                                : false
                        }
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                    >
                        {products.map((product, slideIndex) => (
                            <SwiperSlide
                                key={slideIndex}
                                className={classes.swiperSlide}
                                onClick={() => handleOpenStylePreview(product)}
                            >
                                <div key={product.id} className={classes.productItem}>
                                    <div className={classes.productImageContainer} id="img-container">
                                        <img
                                            src={product?.images?.[0]}
                                            alt={product?.title || 'Product'}
                                            className={classes.productImage}
                                        />
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <EngagingSliderPreview
                    isEngageSliderPreviewOpen={isEngageSliderPreviewOpen}
                    handleCloseStylePreview={handleCloseStylePreview}
                    selectedStyle={selectedStyle.current}
                    handleAnalytics={handleAnalytics}
                />
            </div>
        );
    },
);

EngagingSlider.displayName = 'EngagingSlider';

export default EngagingSlider;
